/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: useSimonSoundman.ts
 * Project: papa-bear
 * -----
 * Last Modified: 30 Nov 2021 5:43:12 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import lodash from 'lodash';
import { VendorModel } from 'models/VendorModel';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
export interface GetVendorsParams {
  dataAreaId: string;
  vendorAccountContains?: string;
  offset: number;
}

export interface GetVendorsResponseModel {
  data: VendorModel[];
  totalCount: number;
}

/* ****************** Variables ****************** */
const grannyFannyNestlerodeBaseUrl = process.env.REACT_APP_SIMON_SOUNDMAN_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_SIMON_SOUNDMAN;

const baseQuery = fetchBaseQuery({
  baseUrl: grannyFannyNestlerodeBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

export const vendorsApi = createApi({
  reducerPath: 'vendorsApi',
  baseQuery,
  endpoints: (builder) => ({
    getVendors: builder.query<GetVendorsResponseModel, GetVendorsParams>({
      query: ({ dataAreaId, vendorAccountContains, offset }) => ({
        url: `vendors`,
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
          vendorAccountContains: vendorAccountContains ? encodeValue({ isUrl: false, value: vendorAccountContains }) : undefined,
          offset
        }
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().vendorsApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useLazyGetVendorsQuery, useGetVendorsQuery, useGetVersionQuery, useGetApiNameQuery } = vendorsApi;
